import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Plus, Play, Eye, Heart, X, Volume2, Maximize2, Upload, Video, Loader2, Film, Trash2, Edit, Image, RefreshCw, Pause, VolumeX, Link, Youtube, Pencil, Check } from 'lucide-react';
import { toast } from 'sonner';

// API URL - dynamically determine at runtime based on window location
const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const RealEstateMediaPortfolio = () => {
  const [activeTab, setActiveTab] = useState('All');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Video upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('story');
  const [uploadSource, setUploadSource] = useState('file'); // 'file' or 'youtube'
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoCategory, setVideoCategory] = useState('Highlights');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedThumbnailFile, setSelectedThumbnailFile] = useState(null);
  const [generatedThumbnail, setGeneratedThumbnail] = useState(null);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editType, setEditType] = useState('');
  const [editYoutubeUrl, setEditYoutubeUrl] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [newThumbnailFile, setNewThumbnailFile] = useState(null);
  const [newVideoFile, setNewVideoFile] = useState(null);
  
  // Videos from API
  const [storyVideos, setStoryVideos] = useState([]);
  const [libraryVideos, setLibraryVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Video player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [showContinueWatchingDialog, setShowContinueWatchingDialog] = useState(false);
  const [savedVideoProgress, setSavedVideoProgress] = useState(0);
  const videoPlayerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const editThumbnailRef = useRef(null);
  const editVideoRef = useRef(null);
  const previewVideoRef = useRef(null);

  // Default media categories
  const defaultMediaCategories = [
    { id: 'All', name: 'All', icon: '📋', color: 'bg-gray-600' },
    { id: 'Highlights', name: 'Highlights', icon: '⭐', color: 'bg-yellow-500' },
    { id: 'Interviews', name: 'Interviews', icon: '🎤', color: 'bg-blue-600' },
    { id: 'Speeches', name: 'Speeches', icon: '🎙️', color: 'bg-purple-600' },
    { id: 'Commercials', name: 'Commercials', icon: '📺', color: 'bg-pink-600' },
    { id: 'Real Estate', name: 'Real Estate', icon: '🏠', color: 'bg-green-600' }
  ];
  
  // Load custom media categories from localStorage
  const [customMediaCategories, setCustomMediaCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('media_categories');
      return saved ? JSON.parse(saved) : defaultMediaCategories;
    } catch { return defaultMediaCategories; }
  });
  
  // Editable category state
  const [showMediaCategoryModal, setShowMediaCategoryModal] = useState(false);
  const [newMediaCategoryName, setNewMediaCategoryName] = useState('');
  const [newMediaCategoryIcon, setNewMediaCategoryIcon] = useState('🎬');
  const [newMediaCategoryColor, setNewMediaCategoryColor] = useState('bg-blue-600');
  const [editingMediaCategory, setEditingMediaCategory] = useState(null);
  const [editMediaCategoryValue, setEditMediaCategoryValue] = useState('');
  
  const mediaCategoryIcons = ['📋', '⭐', '🎤', '🎙️', '📺', '🏠', '🎬', '🎥', '📹', '🎞️', '📽️', '🎭', '🎪', '🎨', '🎵', '🎸', '🎹', '🎧', '📸', '📷'];
  const mediaCategoryColors = ['bg-gray-600', 'bg-yellow-500', 'bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-green-600', 'bg-red-600', 'bg-orange-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-teal-600'];
  
  const addMediaCategory = () => {
    if (!newMediaCategoryName.trim()) return;
    if (customMediaCategories.some(c => c.id === newMediaCategoryName)) { toast.error('Category already exists'); return; }
    const newCat = { id: newMediaCategoryName, name: newMediaCategoryName, icon: newMediaCategoryIcon, color: newMediaCategoryColor };
    const updated = [...customMediaCategories, newCat];
    setCustomMediaCategories(updated);
    localStorage.setItem('media_categories', JSON.stringify(updated));
    setNewMediaCategoryName('');
    setNewMediaCategoryIcon('🎬');
    setNewMediaCategoryColor('bg-blue-600');
    toast.success(`Added "${newMediaCategoryName}" category`);
  };
  
  const updateMediaCategory = (oldId, newName) => {
    if (!newName.trim()) return;
    const updated = customMediaCategories.map(c => c.id === oldId ? { ...c, id: newName, name: newName } : c);
    setCustomMediaCategories(updated);
    localStorage.setItem('media_categories', JSON.stringify(updated));
    if (activeTab === oldId) setActiveTab(newName);
    setEditingMediaCategory(null);
    setEditMediaCategoryValue('');
    toast.success(`Category renamed to "${newName}"`);
  };
  
  const deleteMediaCategory = (categoryId) => {
    if (['All', 'Highlights'].includes(categoryId)) { toast.error('Cannot delete default category'); return; }
    const updated = customMediaCategories.filter(c => c.id !== categoryId);
    setCustomMediaCategories(updated);
    localStorage.setItem('media_categories', JSON.stringify(updated));
    if (activeTab === categoryId) setActiveTab('All');
    toast.success('Category deleted');
  };

  // Use custom categories
  const categories = customMediaCategories.map(c => c.name);
  
  // Generate categoryColors from custom categories
  const categoryColors = customMediaCategories.reduce((acc, cat) => {
    acc[cat.id] = cat.color;
    return acc;
  }, {});

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/media-storage/videos`);
      if (response.ok) {
        const data = await response.json();
        const videos = data.videos || [];
        setStoryVideos(videos.filter(v => v.video_type === 'story' || v.type === 'story'));
        setLibraryVideos(videos.filter(v => v.video_type === 'library' || v.type === 'library'));
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter videos by active category tab
  const filteredStoryVideos = activeTab === 'All' 
    ? storyVideos 
    : storyVideos.filter(v => v.category === activeTab);
  
  const filteredLibraryVideos = activeTab === 'All' 
    ? libraryVideos 
    : libraryVideos.filter(v => v.category === activeTab);

  // Generate thumbnail from video file
  const generateThumbnailFromVideo = useCallback((file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      
      video.onloadeddata = () => {
        video.currentTime = Math.min(1, video.duration / 4); // Capture frame at 1s or 25% of video
      };
      
      video.onseeked = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          resolve(blob);
          URL.revokeObjectURL(video.src);
        }, 'image/jpeg', 0.8);
      };
      
      video.onerror = () => {
        resolve(null);
        URL.revokeObjectURL(video.src);
      };
      
      video.src = URL.createObjectURL(file);
    });
  }, []);

  // Extract YouTube video ID from URL
  const extractYoutubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Handle video file selection with auto thumbnail generation
  const handleVideoSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedVideoFile(file);
      if (!videoTitle) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setVideoTitle(nameWithoutExt);
      }
      
      // Auto-generate thumbnail
      const thumbnailBlob = await generateThumbnailFromVideo(file);
      if (thumbnailBlob) {
        setGeneratedThumbnail(thumbnailBlob);
        setSelectedThumbnailFile(null); // Clear custom thumbnail if auto-generated
      }
    }
  };

  // Handle custom thumbnail selection
  const handleThumbnailSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedThumbnailFile(file);
      setGeneratedThumbnail(null); // Clear auto-generated thumbnail
    }
  };

  // Handle video upload (file or YouTube)
  const handleUploadVideo = async () => {
    if (uploadSource === 'youtube') {
      // YouTube URL upload
      if (!youtubeUrl || !videoTitle) {
        alert('Please enter a YouTube URL and video title');
        return;
      }
      
      const ytId = extractYoutubeId(youtubeUrl);
      if (!ytId) {
        alert('Invalid YouTube URL. Please enter a valid YouTube video link.');
        return;
      }

      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('youtube_url', youtubeUrl);
        formData.append('name', videoTitle);
        formData.append('category', videoCategory);
        formData.append('type', uploadType);

        const response = await fetch(`${API_URL}/api/media-storage/upload-youtube`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const uploadedVideo = await response.json();
          const newVideo = {
            id: uploadedVideo.id,
            title: videoTitle,
            name: videoTitle,
            category: videoCategory,
            categoryColor: categoryColors[videoCategory] || 'bg-purple-600',
            thumbnail: uploadedVideo.thumbnail,
            youtube_url: uploadedVideo.youtube_url,
            youtube_id: uploadedVideo.youtube_id,
            is_youtube: true,
            duration: '0:00',
            views: '0',
            likes: '0',
            type: uploadType,
            video_type: uploadType,
            agent: 'You',
            isNew: true
          };
          
          if (uploadType === 'story') {
            setStoryVideos(prev => [newVideo, ...prev]);
          } else {
            setLibraryVideos(prev => [newVideo, ...prev]);
          }
          
          resetUploadModal();
        } else {
          const error = await response.json();
          alert(`Upload failed: ${error.detail || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        alert('Failed to add YouTube video. Please try again.');
      } finally {
        setIsUploading(false);
      }
      return;
    }

    // File upload
    if (!selectedVideoFile || !videoTitle) {
      alert('Please select a video file and enter a title');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedVideoFile);
      formData.append('name', videoTitle);
      formData.append('category', videoCategory);
      formData.append('type', uploadType);
      
      // Add thumbnail (custom or auto-generated)
      const thumbnailToUpload = selectedThumbnailFile || generatedThumbnail;
      if (thumbnailToUpload) {
        const thumbnailFile = thumbnailToUpload instanceof Blob && !(thumbnailToUpload instanceof File)
          ? new File([thumbnailToUpload], 'thumbnail.jpg', { type: 'image/jpeg' })
          : thumbnailToUpload;
        formData.append('thumbnail', thumbnailFile);
      }

      const response = await fetch(`${API_URL}/api/media-storage/upload`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const uploadedVideo = await response.json();
        const newVideo = {
          id: uploadedVideo.id,
          title: videoTitle,
          name: videoTitle,
          category: videoCategory,
          categoryColor: categoryColors[videoCategory] || 'bg-purple-600',
          thumbnail: uploadedVideo.thumbnail,
          video_url: uploadedVideo.video_url,
          duration: uploadedVideo.duration || '0:00',
          views: '0',
          likes: '0',
          type: uploadType,
          video_type: uploadType,
          agent: 'You',
          isNew: true
        };
        
        if (uploadType === 'story') {
          setStoryVideos(prev => [newVideo, ...prev]);
        } else {
          setLibraryVideos(prev => [newVideo, ...prev]);
        }
        
        resetUploadModal();
      } else {
        const error = await response.json();
        alert(`Upload failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload video. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setSelectedVideoFile(null);
    setSelectedThumbnailFile(null);
    setGeneratedThumbnail(null);
    setVideoTitle('');
    setVideoCategory('Highlights');
    setYoutubeUrl('');
    setUploadSource('file');
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
  };

  // Handle video deletion
  const handleDeleteVideo = async (videoId, videoType, e) => {
    e?.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      const response = await fetch(`${API_URL}/api/media-storage/videos/${videoId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        if (videoType === 'story') {
          setStoryVideos(prev => prev.filter(v => v.id !== videoId));
        } else {
          setLibraryVideos(prev => prev.filter(v => v.id !== videoId));
        }
        if (isModalOpen && selectedVideo?.id === videoId) {
          closeVideoModal();
        }
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  // Open edit modal
  const openEditModal = (video, e) => {
    e?.stopPropagation();
    setEditingVideo(video);
    setEditName(video.name || video.title);
    setEditCategory(video.category);
    setEditType(video.video_type || video.type);
    setEditYoutubeUrl(video.youtube_url || '');
    setNewThumbnailFile(null);
    setNewVideoFile(null);
    setShowEditModal(true);
  };

  // Handle video update
  const handleUpdateVideo = async () => {
    if (!editingVideo) return;
    
    setIsUpdating(true);
    try {
      // Build update payload
      const updatePayload = {
        name: editName,
        category: editCategory,
        video_type: editType
      };
      
      // Add YouTube URL if changed
      if (editYoutubeUrl && editYoutubeUrl !== editingVideo.youtube_url) {
        updatePayload.youtube_url = editYoutubeUrl;
      }
      
      // Update metadata
      const updateResponse = await fetch(`${API_URL}/api/media-storage/videos/${editingVideo.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload)
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update video metadata');
      }

      // Update thumbnail if provided (only for non-YouTube or custom override)
      if (newThumbnailFile) {
        const thumbFormData = new FormData();
        thumbFormData.append('thumbnail', newThumbnailFile);
        await fetch(`${API_URL}/api/media-storage/videos/${editingVideo.id}/thumbnail`, {
          method: 'POST',
          body: thumbFormData
        });
      }

      // Replace video file if provided (only for file-based videos)
      if (newVideoFile && !editingVideo.is_youtube) {
        const videoFormData = new FormData();
        videoFormData.append('file', newVideoFile);
        await fetch(`${API_URL}/api/media-storage/videos/${editingVideo.id}/replace`, {
          method: 'POST',
          body: videoFormData
        });
      }

      // Refresh videos
      await fetchVideos();
      setShowEditModal(false);
      
      // Update selected video if it's the one being edited
      if (selectedVideo?.id === editingVideo.id) {
        setSelectedVideo(prev => ({
          ...prev,
          name: editName,
          title: editName,
          category: editCategory,
          video_type: editType,
          type: editType,
          youtube_url: editYoutubeUrl || prev.youtube_url
        }));
      }
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update video');
    } finally {
      setIsUpdating(false);
    }
  };

  const openVideoModal = (video) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
    setIsPlaying(false);
    setShowControls(true);
    setVideoProgress(0);
    setVideoDuration(0);
    
    // Check for saved progress
    const savedProgress = localStorage.getItem(`video_progress_${video._id || video.id}`);
    if (savedProgress && parseFloat(savedProgress) > 5) {
      setSavedVideoProgress(parseFloat(savedProgress));
      setShowContinueWatchingDialog(true);
    } else {
      setSavedVideoProgress(0);
      setShowContinueWatchingDialog(false);
    }
  };

  const closeVideoModal = () => {
    // Save progress before closing
    if (videoPlayerRef.current && selectedVideo && videoProgress > 5) {
      const videoId = selectedVideo._id || selectedVideo.id;
      localStorage.setItem(`video_progress_${videoId}`, videoProgress.toString());
    }
    
    // Clear controls timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    setIsModalOpen(false);
    setSelectedVideo(null);
    setIsPlaying(false);
    setShowControls(true);
    setShowContinueWatchingDialog(false);
    if (videoPlayerRef.current) {
      videoPlayerRef.current.pause();
    }
  };

  // Handle video tap to show/hide controls (not toggle play)
  const handleVideoTap = (e) => {
    e.stopPropagation();
    setShowControls(prev => !prev);
    
    // Auto-hide controls after 3 seconds if playing
    if (!showControls && isPlaying) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  // Handle play button click
  const handlePlayPause = (e) => {
    e.stopPropagation();
    if (videoPlayerRef.current) {
      if (isPlaying) {
        videoPlayerRef.current.pause();
        setShowControls(true);
      } else {
        videoPlayerRef.current.play();
        // Auto-hide controls after starting playback
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 3000);
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Continue watching handlers
  const handleContinueWatching = () => {
    setShowContinueWatchingDialog(false);
    if (videoPlayerRef.current && savedVideoProgress > 0) {
      videoPlayerRef.current.currentTime = savedVideoProgress;
    }
  };

  const handleStartFromBeginning = () => {
    setShowContinueWatchingDialog(false);
    setSavedVideoProgress(0);
    if (videoPlayerRef.current) {
      videoPlayerRef.current.currentTime = 0;
    }
    // Clear saved progress
    if (selectedVideo) {
      const videoId = selectedVideo._id || selectedVideo.id;
      localStorage.removeItem(`video_progress_${videoId}`);
    }
  };

  // Video time update handler
  const handleTimeUpdate = () => {
    if (videoPlayerRef.current) {
      setVideoProgress(videoPlayerRef.current.currentTime);
      setVideoDuration(videoPlayerRef.current.duration || 0);
    }
  };

  // Progress bar click handler
  const handleProgressBarClick = (e) => {
    e.stopPropagation();
    if (videoPlayerRef.current && videoDuration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * videoDuration;
      videoPlayerRef.current.currentTime = newTime;
      setVideoProgress(newTime);
    }
  };

  // Format time helper
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (videoPlayerRef.current) {
      if (isPlaying) {
        videoPlayerRef.current.pause();
      } else {
        videoPlayerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoPlayerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoPlayerRef.current.requestFullscreen();
      }
    }
  };

  // Get thumbnail URL - handle both custom and generated thumbnails
  const getThumbnailUrl = (video) => {
    if (video.thumbnail) {
      // If it's an API path, prepend the API URL
      if (video.thumbnail.startsWith('/api/')) {
        return `${API_URL}${video.thumbnail}`;
      }
      return video.thumbnail;
    }
    // Default placeholder
    return 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&h=400&fit=crop';
  };

  // Get video URL for playback
  const getVideoUrl = (video) => {
    if (video.video_url) {
      if (video.video_url.startsWith('/api/')) {
        return `${API_URL}${video.video_url}`;
      }
      return video.video_url;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6 overflow-y-auto relative">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold mb-1">Media & Content</h1>
            <p className="text-gray-400 text-xs md:text-sm">Real Estate - Property Media Library</p>
          </div>
          <button 
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2 md:py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-all text-sm md:text-base"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Add New Video</span>
            <span className="sm:hidden">Add Video</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4 md:mb-6">
          <Search className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search videos or content..."
            className="w-full pl-10 md:pl-12 pr-4 py-2.5 md:py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gray-600 text-sm"
          />
        </div>

        {/* Category Tabs */}
        <div className="flex items-center gap-1.5 md:gap-2 mb-4 md:mb-6 overflow-x-auto pb-2">
          {customMediaCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.id)}
              data-testid={`media-category-${cat.id}`}
              className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                activeTab === cat.id ? 'bg-gray-800 text-white' : 'bg-transparent text-gray-400 hover:text-white'
              }`}
            >
              <span>{cat.icon}</span>
              {cat.name}
            </button>
          ))}
          
          {/* Edit Categories Button */}
          <button
            onClick={() => setShowMediaCategoryModal(true)}
            data-testid="edit-media-categories-btn"
            className="px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
          >
            <Pencil size={12} />
            Edit
          </button>
        </div>
      </div>

      {/* Story Section */}
      <div className="mb-6 md:mb-8">
        <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Story ({filteredStoryVideos.length})</h2>
        {isLoading ? (
          <div className="flex items-center justify-center py-8 md:py-12">
            <Loader2 size={28} className="animate-spin text-blue-400" />
          </div>
        ) : filteredStoryVideos.length > 0 ? (
          <div className="flex gap-3 md:gap-4 overflow-x-auto pb-2">
            {filteredStoryVideos.map((video) => (
              <div
                key={video.id}
                onClick={() => openVideoModal(video)}
                className="flex-shrink-0 w-36 md:w-48 bg-gray-900 rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all cursor-pointer relative group"
              >
                {video.isNew && (
                  <div className="absolute top-2 left-2 px-1.5 md:px-2 py-0.5 md:py-1 bg-green-500 rounded text-[10px] md:text-xs font-semibold z-10">NEW</div>
                )}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <button
                    onClick={(e) => openEditModal(video, e)}
                    className="p-1 md:p-1.5 bg-blue-500/80 rounded hover:bg-blue-600"
                  >
                    <Edit size={12} className="text-white" />
                  </button>
                  <button
                    onClick={(e) => handleDeleteVideo(video.id, 'story', e)}
                    className="p-1 md:p-1.5 bg-red-500/80 rounded hover:bg-red-600"
                  >
                    <Trash2 size={12} className="text-white" />
                  </button>
                </div>
                <div className="relative aspect-[9/16]">
                  <img src={getThumbnailUrl(video)} alt={video.title || video.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                    <Play size={36} className="text-white opacity-0 hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="absolute bottom-2 right-2 px-1.5 md:px-2 py-0.5 md:py-1 bg-black bg-opacity-75 rounded text-[10px] md:text-xs font-semibold">
                    {video.duration}
                  </div>
                </div>
                <div className="p-2 md:p-3">
                  <h3 className="font-semibold text-xs md:text-sm mb-1 line-clamp-2">{video.title || video.name}</h3>
                  <p className="text-gray-400 text-[10px] md:text-xs mb-1 md:mb-2">{video.agent}</p>
                  <div className="flex items-center gap-2 md:gap-3 text-[10px] md:text-xs text-gray-400">
                    <span className="flex items-center gap-1"><Eye size={10} /> {video.views}</span>
                    <span className="flex items-center gap-1"><Heart size={10} /> {video.likes}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 md:py-12 bg-gray-900 rounded-lg border border-gray-700">
            <Film size={36} className="mx-auto text-gray-600 mb-3 md:mb-4" />
            <p className="text-gray-400 text-sm md:text-base mb-2">{activeTab === 'All' ? 'No story videos yet' : `No ${activeTab} videos in stories`}</p>
            <p className="text-gray-500 text-xs md:text-sm mb-3 md:mb-4">Upload vertical (9:16) videos for stories</p>
            <button
              onClick={() => { setUploadType('story'); setShowUploadModal(true); }}
              className="px-3 md:px-4 py-1.5 md:py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm"
            >
              Upload Story Video
            </button>
          </div>
        )}
      </div>

      {/* Full Library Section */}
      <div>
        <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Full Library ({filteredLibraryVideos.length})</h2>
        {isLoading ? (
          <div className="flex items-center justify-center py-8 md:py-12">
            <Loader2 size={28} className="animate-spin text-purple-400" />
          </div>
        ) : filteredLibraryVideos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredLibraryVideos.map((video) => (
              <div
                key={video.id}
                onClick={() => openVideoModal(video)}
                className="bg-gray-900 rounded-lg overflow-hidden hover:ring-2 hover:ring-purple-500 transition-all cursor-pointer relative group"
              >
                <div className="relative aspect-video">
                  <img src={getThumbnailUrl(video)} alt={video.title || video.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                    <Play size={48} className="text-white opacity-0 hover:opacity-100 transition-opacity" />
                  </div>
                  {video.isNew && (
                    <div className="absolute top-2 md:top-3 left-2 md:left-3 px-2 md:px-3 py-0.5 md:py-1 bg-green-500 rounded text-[10px] md:text-xs font-semibold">NEW</div>
                  )}
                  <div className={`absolute top-2 md:top-3 ${video.isNew ? 'left-16 md:left-20' : 'left-2 md:left-3'} px-2 md:px-3 py-0.5 md:py-1 ${video.categoryColor || categoryColors[video.category] || 'bg-purple-600'} rounded text-[10px] md:text-xs font-semibold`}>
                    {video.category}
                  </div>
                  <div className="absolute top-2 md:top-3 right-2 md:right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <button
                      onClick={(e) => openEditModal(video, e)}
                      className="p-1 md:p-1.5 bg-blue-500/80 rounded hover:bg-blue-600"
                    >
                      <Edit size={12} className="text-white" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteVideo(video.id, 'library', e)}
                      className="p-1 md:p-1.5 bg-red-500/80 rounded hover:bg-red-600"
                    >
                      <Trash2 size={12} className="text-white" />
                    </button>
                  </div>
                </div>
                <div className="p-3 md:p-4">
                  <h3 className="font-semibold text-sm md:text-base mb-1 line-clamp-2">{video.title || video.name}</h3>
                  <p className="text-gray-400 text-xs md:text-sm mb-2 md:mb-3">{video.agent}</p>
                  <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-gray-400">
                    <div className="flex items-center gap-1"><Eye size={14} /><span>{video.views}</span></div>
                    <div className="flex items-center gap-1"><Heart size={14} /><span>{video.likes}</span></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 md:py-16 bg-gray-900 rounded-lg border border-gray-700">
            <Video size={48} className="mx-auto text-gray-600 mb-3 md:mb-4" />
            <p className="text-gray-400 text-base md:text-xl mb-2">{activeTab === 'All' ? 'No library videos yet' : `No ${activeTab} videos in library`}</p>
            <p className="text-gray-500 text-sm mb-4 md:mb-6">Upload horizontal (16:9) videos to your library</p>
            <button
              onClick={() => { setUploadType('library'); setShowUploadModal(true); }}
              className="px-4 md:px-6 py-2 md:py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors font-semibold text-sm md:text-base"
            >
              Upload Library Video
            </button>
          </div>
        )}
      </div>

      {/* Video Detail Modal with Playback */}
      {isModalOpen && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-2 md:p-4">
          <div className="relative w-full max-w-5xl">
            <button
              onClick={closeVideoModal}
              className="absolute -top-10 md:-top-12 right-0 z-50 p-2 md:p-3 bg-black border-2 border-gray-400 rounded-lg hover:border-white transition-all"
            >
              <X size={20} className="text-white" />
            </button>

            <div className="w-full bg-black border-2 border-gray-400 rounded-xl overflow-hidden max-h-[90vh] overflow-y-auto">
              {/* Video Player */}
              <div className="relative aspect-video bg-gray-950">
                {selectedVideo.is_youtube && selectedVideo.youtube_id ? (
                  // YouTube Embed Player
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=0&rel=0`}
                    title={selectedVideo.title || selectedVideo.name}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : getVideoUrl(selectedVideo) ? (
                  <>
                    {/* Continue Watching Dialog */}
                    {showContinueWatchingDialog && (
                      <div className="absolute inset-0 z-20 bg-black/90 flex items-center justify-center p-4">
                        <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-sm w-full text-center">
                          <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                            <Play size={32} className="text-blue-400" />
                          </div>
                          <h3 className="text-lg font-bold text-white mb-2">Continue Watching?</h3>
                          <p className="text-gray-400 text-sm mb-6">
                            You were at {formatTime(savedVideoProgress)} in this video
                          </p>
                          <div className="flex flex-col gap-3">
                            <button
                              onClick={handleContinueWatching}
                              data-testid="continue-watching-btn"
                              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                            >
                              Continue Watching
                            </button>
                            <button
                              onClick={handleStartFromBeginning}
                              data-testid="start-from-beginning-btn"
                              className="w-full py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                            >
                              Start from Beginning
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <video
                      ref={videoPlayerRef}
                      src={getVideoUrl(selectedVideo)}
                      poster={getThumbnailUrl(selectedVideo)}
                      className="w-full h-full object-contain bg-black"
                      onClick={handleVideoTap}
                      onTimeUpdate={handleTimeUpdate}
                      onEnded={() => {
                        setIsPlaying(false);
                        setShowControls(true);
                        // Clear saved progress when video ends
                        if (selectedVideo) {
                          const videoId = selectedVideo._id || selectedVideo.id;
                          localStorage.removeItem(`video_progress_${videoId}`);
                        }
                      }}
                      onLoadedMetadata={() => {
                        if (videoPlayerRef.current) {
                          setVideoDuration(videoPlayerRef.current.duration);
                        }
                      }}
                    />
                    
                    {/* Controls Overlay - shown on tap */}
                    {showControls && !showContinueWatchingDialog && (
                      <div 
                        className="absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity duration-300"
                        onClick={handleVideoTap}
                      >
                        {/* Semi-transparent background */}
                        <div className="absolute inset-0 bg-black/40"></div>
                        
                        {/* Center Play/Pause Button */}
                        <button
                          onClick={handlePlayPause}
                          data-testid="video-play-pause-btn"
                          className="relative z-10 w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-all transform hover:scale-105"
                        >
                          {isPlaying ? (
                            <Pause size={36} className="text-black" />
                          ) : (
                            <Play size={36} className="text-black ml-1" />
                          )}
                        </button>
                      </div>
                    )}
                    
                    {/* Video Controls Bar - always visible when controls shown */}
                    <div 
                      className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-3 md:p-4 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Progress Bar */}
                      <div 
                        className="w-full h-2 md:h-3 bg-gray-600/80 rounded-full cursor-pointer mb-3 group relative"
                        onClick={handleProgressBarClick}
                        data-testid="video-progress-bar"
                      >
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all relative"
                          style={{ width: videoDuration > 0 ? `${(videoProgress / videoDuration) * 100}%` : '0%' }}
                        >
                          {/* Draggable handle */}
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 md:gap-3">
                        <button 
                          onClick={handlePlayPause} 
                          className="text-white hover:text-blue-400 transition-colors p-1"
                          data-testid="video-controls-play-btn"
                        >
                          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); toggleMute(); }} 
                          className="text-white hover:text-blue-400 transition-colors p-1"
                        >
                          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                        </button>
                        <span className="text-xs md:text-sm text-white font-medium">
                          {formatTime(videoProgress)} / {formatTime(videoDuration)}
                        </span>
                        <div className="flex-1"></div>
                        <button 
                          onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }} 
                          className="text-white hover:text-blue-400 transition-colors p-1"
                        >
                          <Maximize2 size={20} />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <img
                    src={getThumbnailUrl(selectedVideo)}
                    alt={selectedVideo.title || selectedVideo.name}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              {/* Video Details */}
              <div className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row items-start justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg md:text-2xl font-bold mb-2 truncate">{selectedVideo.title || selectedVideo.name}</h2>
                    <div className="flex items-center gap-2 md:gap-4 text-gray-400 flex-wrap">
                      <span className={`px-2 md:px-3 py-0.5 md:py-1 ${categoryColors[selectedVideo.category] || 'bg-purple-600'} rounded text-white text-xs md:text-sm`}>
                        {selectedVideo.category}
                      </span>
                      {selectedVideo.is_youtube && (
                        <span className="px-2 md:px-3 py-0.5 md:py-1 bg-red-600 rounded text-white text-xs md:text-sm flex items-center gap-1">
                          <Youtube size={12} /> YouTube
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-xs md:text-sm"><Eye size={14} /> {selectedVideo.views}</span>
                      <span className="flex items-center gap-1 text-xs md:text-sm"><Heart size={14} /> {selectedVideo.likes}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => openEditModal(selectedVideo, e)}
                    className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-sm"
                  >
                    <Edit size={16} />
                    <span className="hidden sm:inline">Edit Video</span>
                    <span className="sm:hidden">Edit</span>
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-t border-gray-700 pt-4 gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 md:w-12 h-10 md:h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-base md:text-lg font-bold">
                      {(selectedVideo.agent || 'Y').charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-sm md:text-base">{selectedVideo.agent || 'You'}</div>
                      <div className="text-xs md:text-sm text-gray-400">Uploaded by</div>
                    </div>
                  </div>
                  <div className="text-xs md:text-sm text-gray-400">
                    {selectedVideo.is_youtube ? (
                      <a href={selectedVideo.youtube_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline flex items-center gap-1">
                        <Link size={14} /> View on YouTube
                      </a>
                    ) : (
                      `Size: ${selectedVideo.size || 'Unknown'}`
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Video Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-2 md:p-4 z-50" onClick={() => resetUploadModal()}>
          <div className="bg-black border-2 border-gray-400 rounded-xl max-w-lg w-full p-4 md:p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
                <Video className="text-blue-400" size={20} />
                Add New Video
              </h2>
              <button onClick={() => resetUploadModal()} className="text-gray-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Upload Source Selection */}
            <div className="mb-4 md:mb-6">
              <label className="block text-xs md:text-sm font-semibold text-gray-400 mb-2 md:mb-3">Video Source</label>
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                <button
                  onClick={() => setUploadSource('file')}
                  className={`p-2.5 md:p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-1.5 md:gap-2 ${
                    uploadSource === 'file' ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-gray-600 hover:border-gray-500 text-gray-400'
                  }`}
                >
                  <Upload size={18} />
                  <span className="font-semibold text-xs md:text-sm">Upload File</span>
                </button>
                <button
                  onClick={() => setUploadSource('youtube')}
                  className={`p-2.5 md:p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-1.5 md:gap-2 ${
                    uploadSource === 'youtube' ? 'border-red-500 bg-red-500/10 text-red-400' : 'border-gray-600 hover:border-gray-500 text-gray-400'
                  }`}
                >
                  <Youtube size={18} />
                  <span className="font-semibold text-xs md:text-sm">YouTube</span>
                </button>
              </div>
            </div>

            {/* Video Type Selection */}
            <div className="mb-4 md:mb-6">
              <label className="block text-xs md:text-sm font-semibold text-gray-400 mb-2 md:mb-3">Video Type</label>
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                <button
                  onClick={() => setUploadType('story')}
                  className={`p-3 md:p-4 rounded-xl border-2 transition-all ${
                    uploadType === 'story' ? 'border-blue-500 bg-blue-500/10' : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1.5 md:gap-2">
                    <div className="w-10 md:w-12 h-16 md:h-20 bg-gray-800 rounded-lg flex items-center justify-center">
                      <Film size={20} className={uploadType === 'story' ? 'text-blue-400' : 'text-gray-400'} />
                    </div>
                    <span className={`font-semibold text-xs md:text-sm ${uploadType === 'story' ? 'text-blue-400' : 'text-white'}`}>Story</span>
                    <span className="text-[10px] md:text-xs text-gray-400">Vertical (9:16)</span>
                  </div>
                </button>
                <button
                  onClick={() => setUploadType('library')}
                  className={`p-3 md:p-4 rounded-xl border-2 transition-all ${
                    uploadType === 'library' ? 'border-purple-500 bg-purple-500/10' : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1.5 md:gap-2">
                    <div className="w-16 md:w-20 h-10 md:h-12 bg-gray-800 rounded-lg flex items-center justify-center">
                      <Film size={20} className={uploadType === 'library' ? 'text-purple-400' : 'text-gray-400'} />
                    </div>
                    <span className={`font-semibold text-xs md:text-sm ${uploadType === 'library' ? 'text-purple-400' : 'text-white'}`}>Library</span>
                    <span className="text-[10px] md:text-xs text-gray-400">Horizontal (16:9)</span>
                  </div>
                </button>
              </div>
            </div>

            {/* YouTube URL Input */}
            {uploadSource === 'youtube' && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-400 mb-2">YouTube URL *</label>
                <div className="relative">
                  <Youtube className="absolute left-3 top-1/2 transform -translate-y-1/2 text-red-500" size={20} />
                  <input
                    type="text"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>
                {youtubeUrl && extractYoutubeId(youtubeUrl) && (
                  <div className="mt-3 rounded-lg overflow-hidden">
                    <img 
                      src={`https://img.youtube.com/vi/${extractYoutubeId(youtubeUrl)}/mqdefault.jpg`}
                      alt="YouTube thumbnail preview"
                      className="w-full h-auto"
                    />
                    <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
                      <span>✓</span> Valid YouTube URL detected
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Video Upload Area - Only show for file upload */}
            {uploadSource === 'file' && (
              <div 
                className="border-2 border-dashed border-gray-600 rounded-xl p-6 mb-4 text-center hover:border-blue-500 transition-colors cursor-pointer"
                onClick={() => videoInputRef.current?.click()}
              >
                <input
                  ref={videoInputRef}
                  type="file"
                  onChange={handleVideoSelect}
                  className="hidden"
                  accept="video/*"
                />
                {selectedVideoFile ? (
                  <>
                    <Video className="mx-auto text-green-400 mb-3" size={48} />
                    <p className="text-white font-semibold mb-1">{selectedVideoFile.name}</p>
                    <p className="text-gray-400 text-sm mb-3">{(selectedVideoFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                    <button 
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                      onClick={(e) => { e.stopPropagation(); setSelectedVideoFile(null); setGeneratedThumbnail(null); if (videoInputRef.current) videoInputRef.current.value = ''; }}
                    >
                      Change Video
                    </button>
                  </>
                ) : (
                  <>
                    <Upload className="mx-auto text-gray-400 mb-3" size={48} />
                    <p className="text-white font-semibold mb-1">Drag and drop video here</p>
                    <p className="text-gray-400 text-sm mb-3">or click to browse</p>
                    <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                      Browse Files
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Thumbnail Preview & Custom Upload - Only for file uploads */}
            {uploadSource === 'file' && (generatedThumbnail || selectedThumbnailFile) && (
              <div className="mb-4 p-4 bg-gray-900 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-400">Thumbnail Preview</span>
                  <button
                    onClick={() => thumbnailInputRef.current?.click()}
                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <Image size={14} /> Change Thumbnail
                  </button>
                </div>
                <div className="relative aspect-video w-full max-w-xs mx-auto rounded-lg overflow-hidden bg-gray-800">
                  <img
                    src={selectedThumbnailFile ? URL.createObjectURL(selectedThumbnailFile) : (generatedThumbnail ? URL.createObjectURL(generatedThumbnail) : '')}
                    alt="Thumbnail preview"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-xs text-gray-500 text-center mt-2">
                  {selectedThumbnailFile ? 'Custom thumbnail' : 'Auto-generated from video'}
                </p>
              </div>
            )}

            {/* Custom Thumbnail Upload (hidden input) */}
            <input
              ref={thumbnailInputRef}
              type="file"
              onChange={handleThumbnailSelect}
              className="hidden"
              accept="image/*"
            />

            {/* Or upload custom thumbnail button (shown when no video selected yet) - Only for file uploads */}
            {uploadSource === 'file' && !generatedThumbnail && !selectedThumbnailFile && (
              <div className="mb-4">
                <button
                  onClick={() => thumbnailInputRef.current?.click()}
                  className="w-full py-3 border-2 border-dashed border-gray-600 rounded-xl text-gray-400 hover:border-gray-500 hover:text-gray-300 transition-colors flex items-center justify-center gap-2"
                >
                  <Image size={20} />
                  Upload Custom Thumbnail (Optional)
                </button>
              </div>
            )}

            {/* Video Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Video Title *</label>
                <input
                  type="text"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  placeholder="Enter video title"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Category</label>
                <select
                  value={videoCategory}
                  onChange={(e) => setVideoCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                >
                  {categories.filter(c => c !== 'All').map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => resetUploadModal()}
                className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadVideo}
                disabled={isUploading || (uploadSource === 'file' ? !selectedVideoFile : !youtubeUrl) || !videoTitle}
                className={`px-6 py-2.5 text-white rounded-lg transition-all font-semibold flex items-center gap-2 ${
                  uploadSource === 'youtube'
                    ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                    : uploadType === 'story'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                } disabled:from-gray-600 disabled:to-gray-600`}
              >
                {isUploading ? (
                  <><Loader2 size={18} className="animate-spin" />{uploadSource === 'youtube' ? 'Adding...' : 'Uploading...'}</>
                ) : uploadSource === 'youtube' ? (
                  <>
                    <Youtube size={18} />
                    Add YouTube Video
                  </>
                ) : (
                  `Upload ${uploadType === 'story' ? 'Story' : 'to Library'}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Video Modal */}
      {showEditModal && editingVideo && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => setShowEditModal(false)}>
          <div className="bg-black border-2 border-gray-400 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Edit className="text-blue-400" size={24} />
                Edit Video
              </h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>

            {/* YouTube URL - Show if it's a YouTube video or to add YouTube URL */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-400 mb-2">
                YouTube URL {editingVideo.is_youtube && <span className="text-red-400">(YouTube Video)</span>}
              </label>
              <div className="relative">
                <Youtube className="absolute left-3 top-1/2 transform -translate-y-1/2 text-red-500" size={20} />
                <input
                  type="text"
                  value={editYoutubeUrl}
                  onChange={(e) => setEditYoutubeUrl(e.target.value)}
                  placeholder={editingVideo.is_youtube ? "Update YouTube URL" : "Add YouTube URL (converts to YouTube video)"}
                  className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
                />
              </div>
              {editYoutubeUrl && extractYoutubeId(editYoutubeUrl) && editYoutubeUrl !== editingVideo.youtube_url && (
                <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                  <span>✓</span> Valid YouTube URL - will update on save
                </p>
              )}
            </div>

            {/* Current Thumbnail */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-400 mb-2">Thumbnail</label>
              <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-gray-800 mb-2">
                <img
                  src={newThumbnailFile ? URL.createObjectURL(newThumbnailFile) : getThumbnailUrl(editingVideo)}
                  alt="Current thumbnail"
                  className="w-full h-full object-cover"
                />
              </div>
              <input
                ref={editThumbnailRef}
                type="file"
                onChange={(e) => setNewThumbnailFile(e.target.files[0])}
                className="hidden"
                accept="image/*"
              />
              <button
                onClick={() => editThumbnailRef.current?.click()}
                className="w-full py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 flex items-center justify-center gap-2 transition-colors"
              >
                <Image size={16} />
                {newThumbnailFile ? 'Change Thumbnail' : 'Upload New Thumbnail'}
              </button>
            </div>

            {/* Replace Video File - Only show for non-YouTube videos */}
            {!editingVideo.is_youtube && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-400 mb-2">Video File</label>
                <input
                  ref={editVideoRef}
                  type="file"
                  onChange={(e) => setNewVideoFile(e.target.files[0])}
                  className="hidden"
                  accept="video/*"
                />
                <button
                  onClick={() => editVideoRef.current?.click()}
                  className="w-full py-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-gray-500 hover:text-gray-300 transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw size={18} />
                  {newVideoFile ? newVideoFile.name : 'Replace Video File (Optional)'}
                </button>
              </div>
            )}

            {/* Edit Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Video Title</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter video title"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Category</label>
                <select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                >
                  {categories.filter(c => c !== 'All').map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Video Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setEditType('story')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      editType === 'story' ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-gray-600 text-gray-400'
                    }`}
                  >
                    Story (9:16)
                  </button>
                  <button
                    onClick={() => setEditType('library')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      editType === 'library' ? 'border-purple-500 bg-purple-500/10 text-purple-400' : 'border-gray-600 text-gray-400'
                    }`}
                  >
                    Library (16:9)
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between gap-3 mt-6">
              <button
                onClick={() => handleDeleteVideo(editingVideo.id, editingVideo.video_type || editingVideo.type)}
                className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Trash2 size={18} />
                Delete
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateVideo}
                  disabled={isUpdating}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-semibold flex items-center gap-2"
                >
                  {isUpdating ? (
                    <><Loader2 size={18} className="animate-spin" />Saving...</>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Media Category Management Modal */}
      {showMediaCategoryModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowMediaCategoryModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Media Categories</h3>
              <button onClick={() => setShowMediaCategoryModal(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2 mb-3">
                <div className="relative">
                  <button className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center" onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}>{newMediaCategoryIcon}</button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-5 gap-1 w-40">
                    {mediaCategoryIcons.map(icon => (<button key={icon} onClick={(e) => { setNewMediaCategoryIcon(icon); e.currentTarget.parentElement.classList.add('hidden'); }} className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center">{icon}</button>))}
                  </div>
                </div>
                <input type="text" value={newMediaCategoryName} onChange={(e) => setNewMediaCategoryName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addMediaCategory()} placeholder="New category name..." className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" data-testid="new-media-category-input" />
              </div>
              <div className="flex gap-2 items-center mb-3">
                <span className="text-xs text-gray-400">Color:</span>
                <div className="flex gap-1 flex-wrap">
                  {mediaCategoryColors.map(color => (<button key={color} onClick={() => setNewMediaCategoryColor(color)} className={`w-6 h-6 rounded-full ${color} ${newMediaCategoryColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''}`} />))}
                </div>
              </div>
              <button onClick={addMediaCategory} disabled={!newMediaCategoryName.trim()} className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5" data-testid="add-media-category-btn"><Plus size={14} />Add Category</button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {customMediaCategories.filter(c => c.id !== 'All').map(cat => (
                <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group">
                  {editingMediaCategory === cat.id ? (
                    <>
                      <span className="text-lg">{cat.icon}</span>
                      <input type="text" value={editMediaCategoryValue} onChange={(e) => setEditMediaCategoryValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') updateMediaCategory(cat.id, editMediaCategoryValue); if (e.key === 'Escape') { setEditingMediaCategory(null); setEditMediaCategoryValue(''); }}} className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm" autoFocus />
                      <button onClick={() => updateMediaCategory(cat.id, editMediaCategoryValue)} className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"><Check size={14} /></button>
                      <button onClick={() => { setEditingMediaCategory(null); setEditMediaCategoryValue(''); }} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center text-sm`}>{cat.icon}</span>
                      <span className="flex-1 text-white text-sm">{cat.name}{cat.id === 'Highlights' && <span className="ml-2 text-xs text-gray-500">(default)</span>}</span>
                      <button onClick={() => { setEditingMediaCategory(cat.id); setEditMediaCategoryValue(cat.name); }} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100"><Pencil size={14} /></button>
                      {cat.id !== 'Highlights' && <button onClick={() => deleteMediaCategory(cat.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-700">
              <button onClick={() => setShowMediaCategoryModal(false)} className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium" data-testid="done-media-category-btn">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateMediaPortfolio;
